import {useTranslation} from "react-i18next";
import React, {useState} from "react";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader, CircularProgress, Divider,
    Grid, IconButton, InputAdornment, Link, Stack,
    Tab,
    Tabs, TextField,
    Typography,
    useTheme
} from "@mui/material";
import {authClient} from "./App.jsx";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import GoogleIcon from "@mui/icons-material/Google";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import GitHubIcon from "@mui/icons-material/GitHub";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PersonOutline from "@mui/icons-material/PersonOutline";

import PasswordField from "./PasswordField";

const Login = () => {

    const [tabValue, setTabValue] = useState("login");

    const {t} = useTranslation();

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [message, setMessage] = useState("");
    const [fullName, setFullName] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [provider, setProvider] = useState("google");
    const [idToken, setIdToken] = useState(""); 

    const theme = useTheme();

    const handleRedirect = async (context) => {
            // If the response is a redirect, follow it
            if (context.data?.redirect && context.data?.url) {
                window.location.href = context.data.url;
                }
            }

    const fetchOptions = { onSuccess: handleRedirect,
                                                    headers: {
                                                        accept: "application/json", // ✅ force JSON redirect response (avoid 302 in fetch)
                                                        }
                                                    };

    const getOauthQuery = () => {
        // Return a raw querystring (NO leading '?') so oauth-provider can parse it.
        const params = new URLSearchParams(window.location.search);

        // If there is a `sig` param, include params up to and including `sig`
        // (mirrors oauth-provider client behavior).
        if (params.has("sig")) {
            const signedParams = new URLSearchParams();
            for (const [key, value] of params.entries()) {
                signedParams.append(key, value);
                if (key === "sig") break;
                }
            const qs = signedParams.toString();
            return qs || undefined;
            }

        // Otherwise pass all query params (still without '?')
        const qs = params.toString();
        return qs || undefined;
        };
    
    const continueOauthIfNeeded = async () => {
      const oauth_query = getOauthQuery(); // must be the raw querystring WITHOUT leading '?'
      if (!oauth_query) return false;
    
      const url = `/api/auth/oauth2/continue`;
    
      const res = await fetch(url, {
        method: "POST",
        headers: {
            accept: "application/json",
            "content-type": "application/json"
            },
        credentials: "include",
        body: JSON.stringify({ oauth_query, postLogin: true })
        });
      
      const data = await res.json().catch(() => ({}));
      if (data?.redirect && data?.uri) {
        window.location.href = data.uri;
        return true;
        }
      return false;
        };



    const handleChange = (event) => {
        const {name, value} = event.target;
        switch (name) {
            case "name": setName(value); break;
            case "email": setEmail(value); break;
            case "password": setPassword(value); break;
            case "provider": setProvider(value); break;
            case "idToken": setIdToken(value); break;
            }
        }

    const handleSignIn = async (event) => {

        event.preventDefault();
        if (tabValue == "signup") {
            await handleSignUp();
            }
        const {data, error} = await authClient.signIn.email({email,
                                                            password,
                                                            oauth_query: getOauthQuery(),
                                                            fetchOptions
                                                            });

        setMessage(data?.message || error?.message);
        }
        
    const handleSignInWithToken = async (event) => {

        event.preventDefault();
        
        const {data, error} = await authClient.signIn.social({provider, idToken: {token: idToken},
                                                            fetchOptions
                                                            });

        setMessage(data?.message || error?.message);
        }


    const handleSignUp = async () => {
        const {data, error} = await authClient.signUp.email({email, password, name, redirectTo: document.location.origin});
        setMessage(data?.message || error?.message);
        console.log(data, error);
        }

    const handleAnonymousSignIn = async () => {
        const {data, error} = await authClient.signIn.anonymous({ oauth_query: getOauthQuery(), fetchOptions});
        setMessage(data?.message || error?.message);
        console.log(data, error);
        }

    const handlePasskey = async () => {
        const {data, error} = await authClient.signIn.passkey({autoFill: false, oauth_query: getOauthQuery(),fetchOptions});
        setMessage(data?.message || error?.message);
        console.log(data, error);
         if (!error) {
            await continueOauthIfNeeded();
            }
        }

    const handleRequestReset = async () => {
        const {data, error} = await authClient.requestPasswordReset({email, redirectTo: document.location.origin + "/resetPassword"});
        setMessage(data?.message || error?.message);
        console.log(data, error);
        }

    const handleMessage = (message) => {
        setMessage(message);
        }

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event) => event.preventDefault();

    return <Box>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', pr: 8 }}>
                    <Tabs
                        value={tabValue}
                        onChange={(e, v) => setTabValue(v)}
                        variant="fullWidth"
                        indicatorColor="primary"
                        textColor="primary">
                        <Tab value="login" icon={<LoginIcon />} iconPosition="start" label={t('Sign In')} />
                        <Tab value="signup" icon={<PersonAddIcon />} iconPosition="start" label={t('Sign Up')} />
                    </Tabs>
                </Box>

                <CardContent  sx={{ p: 4 }}>

                    <Button fullWidth variant="outlined" startIcon={<FingerprintIcon />} color="inherit" size="large"
                            sx={{ mb: 2, justifyContent: 'flex-start', px: 3, borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)' }}
                            onClick={handlePasskey}>
                        {t('Sign in with Passkey')}
                    </Button>

                    <Grid container spacing={1.5} sx={{ mb: 3 }}>
                        <SocialLoginButton provider={"google"} onMessage={handleMessage} icon={<GoogleIcon />}>Google</SocialLoginButton>
                        <SocialLoginButton provider={"github"} onMessage={handleMessage} icon={<GitHubIcon />}>GitHub</SocialLoginButton>
                        <SocialLoginButton provider={"microsoft"} onMessage={handleMessage} icon={<MicrosoftIcon />}>Microsoft</SocialLoginButton>
                        <SocialLoginButton provider={"linkedin"} onMessage={handleMessage} icon={<LinkedInIcon />}>LinkedIn</SocialLoginButton>
                    </Grid>

                    <Divider sx={{ my: 3 }}>
                        <Typography variant="caption" color="text.secondary">{t('or email')}</Typography>
                    </Divider>

                    <Box component="form" onSubmit={handleSignIn}>
                        <Stack spacing={2}>
                            {tabValue === "signup" && (
                                <TextField required fullWidth name="name" onChange={handleChange} value={name} label={t('Full Name')} />
                                )}

                            <TextField
                                required
                                fullWidth
                                name={"email"}
                                label={t('Email')}
                                type="email"
                                value={email}
                                onChange={handleChange}
                                />

                            <PasswordField
                                name={"password"}
                                onChange={handleChange}
                                label={t('Password')}
                                value={password}
                                />

                            {tabValue === "login" && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Link component="button" type="button" variant="body2" onClick={handleRequestReset} underline="hover">
                                        {t('Forgot password?')}
                                    </Link>
                                </Box>
                                )}

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                size="large"
                                disabled={isLoading}
                                sx={{ py: 1.5 }}>
                                {isLoading ? <CircularProgress size={24} color="inherit" /> : (tabValue === "login" ? t('Sign In') : t('Sign Up'))}
                            </Button>
                        </Stack>
                    </Box>

                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
                        {tabValue === "login" ? t('Don\'t have an account? ') + " " : t('Already have an account?') + " "}
                        <Link
                            component="button"
                            variant="body2"
                            fontWeight="bold"
                            onClick={() => setTabValue(tabValue === "login" ? "signup" : "login")}
                            >
                            {tabValue === "login" ? t('Sign Up') : t('Sign In')}
                        </Link>
                    </Typography>


                   <Divider sx={{ my: 3 }}>
                        <Typography variant="caption" color="text.secondary">{t('or annonymous')}</Typography>
                    </Divider>

                    <Button fullWidth variant="outlined" startIcon={<PersonOutline />} color="inherit" size="large"
                            sx={{ mb: 2, justifyContent: 'flex-start', px: 3, borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)' }}
                            onClick={handleAnonymousSignIn}>
                        {t('Sign in anonymous')}
                    </Button>
                    
                    <Divider sx={{ my: 3 }}>
                        <Typography variant="caption" color="text.secondary">{t('or ID Token')}</Typography>
                    </Divider>

                    
                    <TextField
                                required
                                fullWidth
                                name={"provider"}
                                label={t('Provider')}
                                type="text"
                                value={provider}
                                onChange={handleChange}
                                />
                    
                    <TextField
                                required
                                fullWidth
                                name={"idToken"}
                                label={t('ID Token')}
                                type="text"
                                value={idToken}
                                onChange={handleChange}
                                />


                    <Button fullwidth onClick={handleSignInWithToken} variant="contained">ID Token Sign in</Button>
                    
                    <p style={{color: "red"}}>{message}</p>

                </CardContent>

            </Box>;
    }

const SocialLoginButton = ({provider, icon, onMessage, children}) => {

    const handleRedirect = async (context) => {
				// If the response is a redirect, follow it
				if (context.data?.redirect && context.data?.url) {
					window.location.href = context.data.url;
				    }
			    }

    const fetchOptions = { onSuccess: handleRedirect};

    const handleSocialSignIn = async () => {
        const {data, error} = await authClient.signIn.social({
                provider,
                //callbackURL: document.location.origin,
                authQuery: {prompt: "login"},
                fetchOptions
                });
        onMessage(data?.message || error?.message);
        }

    return <Grid item xs={6}>
            <Button fullWidth variant="outlined" startIcon={icon} onClick={handleSocialSignIn} >
                {children}
            </Button>
          </Grid>
    }

export default Login;